import { type DemandManagementStatus, Status, UiTemplate } from '@api/generated/models';
import { withTranslation } from '@application/i18n/with_translation';
import { I18nAndL10n } from '@modules/i18n_and_l10n/mod';
import { DateDisplayFormats, DateService } from '@services/date.service';
import type { AccessFlow } from '@services/flexible_component/mod';
import { DemandManagementApiService } from '@src/api/demand_management_api.service';
import { useCurrentLocation } from '@src/application/use_current_location';
import { useQuery } from '@tanstack/react-query';
import { useService } from '@visiba/instantiation';

export function useDemandManagementStatus(): AccessFlow {
	const [location] = useCurrentLocation();
	const demandManagementApiService = useService(DemandManagementApiService);
	const dateService = useService(DateService);
	const receptionId = location?.receptionId ?? null;

	const messagingQuery = useQuery({
		queryKey: ['demand-management-messaging-open-status', receptionId],
		queryFn: async () => (await demandManagementApiService.getMessagingOpenStatus(receptionId ?? 0)).data,
		enabled: receptionId != null,
	});
	const triageQuery = useQuery({
		queryKey: ['demand-management-triage-open-status', receptionId],
		queryFn: async () => (await demandManagementApiService.getTriageOpenStatus(receptionId ?? 0)).data,
		enabled: receptionId != null,
	});

	return {
		messaging: toAccessFlowStatus(messagingQuery.data ?? null, dateService),
		triage: toAccessFlowStatus(triageQuery.data ?? null, dateService),
	};
}

function toAccessFlowStatus(demandManagementStatus: DemandManagementStatus | null, dateService: DateService): AccessFlow['messaging'] {
	if (demandManagementStatus?.NextEvent == null) {
		return {
			isOpen: false,
			text: '',
			status: Status.Unavailable,
		};
	}

	const translationKey = getOpeningHoursStatusTranslationKey(demandManagementStatus.UiTemplate);
	const nextEvent = dateService.newDate(demandManagementStatus.NextEvent);

	if (demandManagementStatus.UiTemplate === UiTemplate.OpensWeekday) {
		return {
			isOpen: demandManagementStatus.Status === Status.Open,
			text: withTranslation(translationKey, {
				day: withTranslation(getWeekdayTranslationKey(nextEvent)),
				time: nextEvent.format(dateService.getLocaleSpecificDateFormatTemplate(DateDisplayFormats.JUST_TIME)),
			}),
			status: demandManagementStatus.Status,
		};
	}

	return {
		isOpen: demandManagementStatus.Status === Status.Open,
		text:
			demandManagementStatus.UiTemplate === UiTemplate.Open
				? withTranslation(translationKey)
				: withTranslation(translationKey, {
						time: nextEvent.format(dateService.getLocaleSpecificDateFormatTemplate(DateDisplayFormats.JUST_TIME)),
					}),
		status: demandManagementStatus.Status,
	};
}

function getOpeningHoursStatusTranslationKey(uiTemplate: UiTemplate): I18nAndL10n.LocaleKey {
	switch (uiTemplate) {
		case UiTemplate.Open:
			return 'DropIn.OpeningHours.Hours.Open';
		case UiTemplate.OpensToday:
			return 'DemandManagement.OpeningHours.Status.OpensToday';
		case UiTemplate.OpensTomorrow:
			return 'DemandManagement.OpeningHours.Status.OpensTomorrow';
		case UiTemplate.OpensWeekday:
			return 'DemandManagement.OpeningHours.Status.OpensWeekday';
		case UiTemplate.ClosesToday:
			return 'DemandManagement.OpeningHours.Status.ClosesToday';
		case UiTemplate.Closed:
			return 'DemandManagement.OpeningHours.Status';
	}
}

function getWeekdayTranslationKey(
	date: VisibaDate,
):
	| 'Common.Weekday.Sunday'
	| 'Common.Weekday.Monday'
	| 'Common.Weekday.Tuesday'
	| 'Common.Weekday.Wednesday'
	| 'Common.Weekday.Thursday'
	| 'Common.Weekday.Friday'
	| 'Common.Weekday.Saturday' {
	switch (date.day()) {
		case 0:
			return 'Common.Weekday.Sunday';
		case 1:
			return 'Common.Weekday.Monday';
		case 2:
			return 'Common.Weekday.Tuesday';
		case 3:
			return 'Common.Weekday.Wednesday';
		case 4:
			return 'Common.Weekday.Thursday';
		case 5:
			return 'Common.Weekday.Friday';
		case 6:
			return 'Common.Weekday.Saturday';
		default:
			return 'Common.Weekday.Monday';
	}
}
